import {
  Inject,
  Injectable,
  Optional,
  computed,
  effect,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WSClient } from '@ay-nestjs/share-client';
import { ExposedPromise } from '@ay/util';
import { debounceTime, map } from 'rxjs';

const CONNECTED = '連線至伺服器';
const CONNECTING = '連線中';

@Injectable({
  providedIn: 'root',
})
export class ConnectionService {
  public server: string;
  public status = signal('正在連線至伺服器');
  public status$ = toObservable(this.status);
  public notifyStatus: boolean;

  public connected$ = this.status$.pipe(map((_) => _ === CONNECTED));
  public isConnected = computed(() => this.status() === CONNECTED);
  public connected!: Promise<void>;

  private _matSnackBarOption = { duration: 500 };

  public constructor(
    @Inject('WSC') wsc: WSClient,
    @Inject('SERVER_URLS') serverUrls: string[],
    public matSnackBar: MatSnackBar,
    @Optional()
    @Inject('NOTIFY_CONNECTION_STATUS')
    _notifyStatus: boolean | null,
  ) {
    this.notifyStatus = _notifyStatus ?? false;

    let ep = new ExposedPromise<void>();
    this.connected = ep.promise;

    wsc.status.subscribe(async (status) => {
      // 當網路中斷時間很短時，socket.io 可能在內部就已經完成重連過程，因此當狀態回復時會直接觸發 connect 事件
      // ，而不是先觸發 reconnecting 再 reconnect。
      // 但對伺服器來說，這樣的行為是斷線重連而不是第一次 connect，
      // 因此當狀態回復時，先恢復為 false，再恢復為 true, 才能正確 loginViaToken
      const connectedTwice =
        this.status() === CONNECTED && status === CONNECTED;
      if (connectedTwice) {
        this.status.set(CONNECTING);
        await new Promise((resolve) => setTimeout(resolve, 0));
        if (this.status() === CONNECTED) return;
      }
      this.status.set(status);
    });

    wsc.status.pipe(debounceTime(100)).subscribe(async (status) => {
      // 如果連線成功，且有等待中的 promise，則 resolve
      if (status === CONNECTED) {
        this.server = wsc.connectedServer;
        if (ep.isPending()) {
          ep.resolve();
        }

        // 如果連線失敗， promise 已經被 resolved ，則建立一個新的 promise
      } else if (!ep.isPending()) {
        ep = new ExposedPromise<void>();
        this.connected = ep.promise;
      }
    });

    effect(() => {
      if (!this.notifyStatus) return;

      const status = this.status();
      const event = { type: status, target: this.server };
      console.info(
        $localize`[連線狀態] ${event.type} ${
          event.target ? `: ${event.target}` : ''
        }`,
      );

      switch (event.type) {
        case '伺服器皆無回應':
          this.matSnackBar.open(
            $localize`伺服器皆無回應，請確認網路連線狀況`,
            '',
            this._matSnackBarOption,
          );
          break;

        case '嘗試重新連線':
          this.matSnackBar.open(
            $localize`嘗試重新連線`,
            '',
            this._matSnackBarOption,
          );
          break;

        case '連線至伺服器':
          this.matSnackBar.open(
            $localize`嘗試與伺服器連線`,
            '',
            this._matSnackBarOption,
          );
          break;
      }
    });

    wsc.connect(...serverUrls);
  }

  public async awaitConnected() {
    return await this.connected;
  }
}
